import { MatDialog } from '@angular/material/dialog';
import { Injectable } from '@angular/core';
import { ApiService } from 'app/shared/apis/api.service';
import { BaseService } from 'app/core/services/base.service';
import { VehicleModel } from 'app/models/vehicle.model';
import { Observable } from 'rxjs';
import { formatDateYmdLocal } from 'app/shared/utils/local-calendar-date';

export interface VehicleRealtimeMapKpis {
    fuel_consumption_liters: number;
    km_traveled: number;
    km_per_liter: number;
    speed: {
        min: number | null;
        max: number | null;
        average: number | null;
    };
}

export interface VehicleRealtimeMapPosition {
    id: number;
    vehicle_id: number;
    service_id: number | null;
    provider?: 'GEOTAB' | 'HIKVISION' | 'OTHER' | string | null;
    device_id?: string | null;
    timestamp: string | Date;
    latitude: number | null;
    longitude: number | null;
    speed: number | null;
    direction: number | null;
    altitude?: number | null;
    odometer?: number | null;
    fuel_consumption?: number | null;
    fuel_level?: number | null;
    created_at?: string | Date;
}

export interface VehicleRealtimeMapActiveService {
    service_id: number;
    start: string | Date | null;
    end: string | Date | null;
    type_service_id?: number | null;
    type_service?: { type_service_id: number; name: string | null } | null;
}

export interface VehicleRealtimeMapItem {
    vehicle: VehicleModel;
    lastPosition: VehicleRealtimeMapPosition | null;
    activeService: VehicleRealtimeMapActiveService | null;
    currentServiceKpis: VehicleRealtimeMapKpis | null;
    known_location_name?: string | null;
}

export interface VehicleRealtimeMapResponse {
    items: VehicleRealtimeMapItem[];
}

@Injectable({
    providedIn: 'root',
})
export class VehicleService extends BaseService<VehicleModel> {
    constructor(api: ApiService, _matDialog: MatDialog) {
        super(api, _matDialog, 'vehicle');
    }

    getStock(vehicle_id: number, company_id: number): Observable<any> {
        return this.api.getSub(`${this.endpoint}/${vehicle_id}/stock`, { company_id });
    }

    getSupplyMovements(vehicle_id: number, company_id: number): Observable<any> {
        return this.api.getSub(`${this.endpoint}/${vehicle_id}/supply-movements`, { company_id });
    }

    getLastPosition(vehicle_id: number): Observable<any> {
        return this.api.getSub(`${this.endpoint}/${vehicle_id}/last-position`);
    }

    getRealtimeMap(company_id: number) {
        const $request = this.api.getSub<VehicleRealtimeMapResponse>(`${this.endpoint}/realtime-map`, { company_id });
        return this.withPromise($request);
    }

    getAllRealtimeMap() {
        const $request = this.api.getSub<VehicleRealtimeMapResponse>(`${this.endpoint}/admin/all-realtime-map`);
        return this.withPromise($request);
    }

    getMeasurements(vehicle_id: number, fromDate?: Date, toDate?: Date) {
        const params: any = {};
        if (fromDate) {
            params.fromDate = formatDateYmdLocal(fromDate);
        }
        if (toDate) {
            params.toDate = formatDateYmdLocal(toDate);
        }
        const $request = this.api.getSub<any>(`${this.endpoint}/${vehicle_id}/measurements`, params);
        return this.withPromise($request);
    }

    getSecurityEvents(vehicle_id: number, fromDate?: Date, toDate?: Date) {
        const params: any = {};
        if (fromDate) {
            params.fromDate = formatDateYmdLocal(fromDate);
        }
        if (toDate) {
            params.toDate = formatDateYmdLocal(toDate);
        }
        const $request = this.api.getSub<any>(`${this.endpoint}/${vehicle_id}/security-events`, params);
        return this.withPromise($request);
    }

    getHomeStatus(vehicle_id: number) {
        const $request = this.api.getSub<any>(`${this.endpoint}/${vehicle_id}/home-status`);
        return this.withPromise($request);
    }

    repairTelemetry(vehicleId: number, fromDate: Date, toDate: Date, includeEvents = true) {
        const body = {
            fromDate: formatDateYmdLocal(fromDate),
            toDate: formatDateYmdLocal(toDate),
            includeEvents,
        };
        const $request = this.api.postSub<any>(`${this.endpoint}/${vehicleId}/repair-telemetry`, body);
        return this.withPromise($request);
    }

    updateSpecsBulk(
        companyId: number,
        items: VehicleSpecsBulkItem[],
    ): Observable<{ vehicles: VehicleModel[] }> {
        return this.api.putSub<{ vehicles: VehicleModel[] }>(`${this.endpoint}/specs/bulk`, {
            company_id: companyId,
            items,
        });
    }

    upsertSpecs(vehicleId: number, body: VehicleSpecsUpsertBody): Observable<VehicleModel> {
        return this.api.putSub<VehicleModel>(`${this.endpoint}/${vehicleId}/specs`, body);
    }

    getFleetAnalytics(
        filter: FleetAnalyticsFilter,
    ): Observable<FleetAnalyticsResult> & { promise: Promise<FleetAnalyticsResult> } {
        const $request = this.api.getSub<FleetAnalyticsResult>(`${this.endpoint}/fleet-analytics`, filter as any);
        return this.withPromise($request);
    }
}

export interface FleetAnalyticsFilter {
    company_id: number;
    vehicle_id?: number;
    vehicle_ids?: string;
    start_date: string;
    end_date: string;
    granularity: 'hour' | 'day' | 'week' | 'month';
    mode: 'all' | 'in_service' | 'out_of_service';
}

export interface FleetAnalyticsResult {
    kpis: {
        total_km: number;
        vehicle_count: number;
        avg_km_per_vehicle: number;
        total_liters: number;
        avg_liters_per_100km: number;
    };
    series: Array<{
        period: string;
        label: string;
        km: number;
        liters: number;
        liters_per_100km: number;
    }>;
    top_vehicles: Array<{
        vehicle_id: number;
        name: string;
        license_plate: string | null;
        km: number;
        liters: number;
        liters_per_100km: number;
    }>;
}

export interface VehicleSeatInput {
    seat_type: 'DRIVER' | 'NORMAL' | 'RESCUER';
    label?: string | null;
    sort_order?: number;
}

export interface VehicleSpecsUpsertBody {
    company_id?: number;
    brand?: string | null;
    model?: string | null;
    vehicle_type?: string | null;
    has_stretcher?: boolean | null;
    has_wheelchair?: boolean | null;
    has_oxygen?: boolean | null;
    stretcher_seat_penalty?: number | null;
    wheelchair_seat_penalty?: number | null;
    conversion_seat_type?: 'NORMAL' | 'RESCUER' | 'ANY' | null;
    seats?: VehicleSeatInput[];
}

export interface VehicleSpecsBulkItem {
    vehicle_id: number;
    brand?: string | null;
    model?: string | null;
    vehicle_type?: string | null;
    seats_total?: number | null;
    passenger_capacity?: number | null;
    seats_crew?: number | null;
    stretcher_capacity?: number | null;
    wheelchair_capacity?: number | null;
    seats_converted?: boolean | null;
    has_oxygen?: boolean | null;
    stretcher_seat_penalty?: number | null;
    wheelchair_seat_penalty?: number | null;
    conversion_seat_type?: 'NORMAL' | 'RESCUER' | 'ANY' | null;
}
